// src/portal-app/shell/kit/PullToRefresh.js
//
// Pull-to-refresh primitive for portal-app list screens (spec §8). Built
// once here so nineteen P4 screens don't each build the gesture, spring
// animation and a11y contract from scratch — see the deferral note this
// replaces at operations/TransactionsScreen.js:50-53.
//
// The root node is NOT the scroll container — it is a plain block in normal
// flow, and the gesture reads its scroll position from the nearest scrollable
// ANCESTOR (resolveScrollHost below). This matches how the app is actually
// built: every screen mounts inside a host-owned scroller — `main.psh-main`
// (PortalShell.js, tab roots) or `.pnav-body` (NavStack.js, pushed screens) —
// and every screen root is a plain block wrapper. An earlier revision made
// `.pk-ptr` itself `overflow-y:auto;flex:1 1 auto`, which only becomes a
// scroll box under a bounded flex parent; nested in a block ancestor it sat
// at content height, so its own scrollTop was permanently 0, the arm guard
// below never rejected, and every upward drag anywhere in a list was
// preventDefault()-ed into a refresh. Reading from the real scroller removes
// that assumption entirely: this works wrapped, nested, or standalone, so
// future screens cannot re-introduce the bug by mounting it "wrong".
//
// Contract:
//   onRefresh   {fn}    () => Promise<any>. Spinner shows until it settles,
//                        including on reject (try/finally — a rejected
//                        promise must not leave a permanent spinner).
//   disabled    {bool}  suppresses the gesture entirely (e.g. a sheet is open).
//   children    {node}
//
// Gesture rules:
//   - Only arms on touchstart when the enclosing scroller's scrollTop <= 0 —
//     mid-list drags scroll normally instead of fighting the pull.
//   - ~64px of pull before release triggers a refresh; below that, the
//     indicator springs back and onRefresh is never called.
//   - A refresh in flight blocks a new gesture from arming at all
//     (refreshingRef), so pulls can never fire concurrently.
//
// Haptics: no Capacitor Haptics helper exists anywhere in the repo, and
// @capacitor/haptics is not an installed dependency (checked package.json) —
// so there is nothing to reuse. The codebase's own established pattern for
// this kind of feedback is a guarded `navigator.vibrate` call (see
// pos-app's boardFlow.js and pages/pos/OrderViewPage.js: `if
// (navigator.vibrate) navigator.vibrate(...)`), which also degrades safely
// since jsdom and most browsers simply omit the API. Followed the same
// pattern here, wrapped in try/catch as well so it can never throw into the
// refresh flow.
//
// Native scroll suppression: touchmove is deliberately NOT a JSX prop.
// React 17+ registers onTouchStart/onTouchMove as passive at the root for
// scroll-performance reasons, so a preventDefault() inside a JSX handler
// would not reliably stop native overscroll bounce on a real device. The
// listener below is attached directly via addEventListener with
// { passive: false } so preventDefault() actually works, and is scoped as
// tightly as the armedRef/rawDelta guard allows — see handleNativeTouchMove.
import React, {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { ensureKitCss } from './kitCss';

const THRESHOLD = 64;
const MAX_PULL = 90;

/* Is this node the box that actually scrolls? Both conditions matter: an
   overflow:auto box whose content fits doesn't scroll (scrollTop is pinned to
   0 and the real scroller is further up), and a box that overflows without an
   auto/scroll overflow-y just clips. */
function isScrollable(node) {
    if (!node || node.nodeType !== 1) return false;
    if (typeof window === 'undefined' || typeof window.getComputedStyle !== 'function') return false;
    const overflowY = window.getComputedStyle(node).overflowY;
    if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') return false;
    return node.scrollHeight > node.clientHeight;
}

/* Nearest scrollable ancestor, starting at the element itself so this is
   still correct if PullToRefresh is ever given a bounded parent and becomes
   the scroller in its own right. Falls back to the element when nothing in
   the chain scrolls — a list shorter than the viewport is at the top by
   definition, so scrollTop 0 is the right answer there. */
function resolveScrollHost(el) {
    if (typeof document === 'undefined') return el;
    let node = el;
    while (node && node !== document.body && node !== document.documentElement) {
        if (isScrollable(node)) return node;
        node = node.parentElement;
    }
    return el;
}

function triggerHaptic() {
    try {
        if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function') {
            navigator.vibrate(10);
        }
    } catch (_e) {
        // Haptics are a nice-to-have; never let them break a refresh.
    }
}

export function PullToRefresh({ onRefresh, disabled = false, children }) {
    ensureKitCss();
    const { t } = useLanguage();

    const containerRef = useRef(null);
    const armedRef = useRef(false);
    const startYRef = useRef(0);
    const pullYRef = useRef(0);
    const refreshingRef = useRef(false);

    const [visualPull, setVisualPull] = useState(0);
    const [dragging, setDragging] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const cancelGesture = useCallback(() => {
        armedRef.current = false;
        pullYRef.current = 0;
        setVisualPull(0);
        setDragging(false);
    }, []);

    const handleTouchStart = useCallback((e) => {
        if (disabled || refreshingRef.current) return;
        const el = containerRef.current;
        if (!el) return;
        // Resolved per gesture, not cached: a screen can swap its scroll host
        // between renders (tab root vs pushed screen) and the walk is a
        // handful of parentElement hops on a touchstart — not a hot path.
        const host = resolveScrollHost(el);
        if (host.scrollTop > 0) return;
        const touch = e.touches && e.touches[0];
        if (!touch) return;
        armedRef.current = true;
        startYRef.current = touch.clientY;
        setDragging(true);
    }, [disabled]);

    // Native (non-passive) touchmove handler — see the file-header note on
    // why this can't be a JSX onTouchMove prop. preventDefault() only fires
    // when the gesture is armed (disabled / mid-list / refresh-in-flight
    // already rule that out via armedRef — see handleTouchStart) AND the
    // finger is actually moving down (rawDelta > 0). An upward move while
    // armed is a normal scroll-into-the-list and must be left alone, which
    // is why the raw (unclamped, possibly negative) delta is checked here
    // rather than the floored value used for the visual pull distance.
    const handleNativeTouchMove = useCallback((e) => {
        if (!armedRef.current) return;
        const touch = e.touches && e.touches[0];
        if (!touch) return;
        const rawDelta = touch.clientY - startYRef.current;
        if (rawDelta > 0 && e.cancelable) {
            e.preventDefault();
        }
        const delta = Math.max(0, rawDelta);
        const clamped = Math.min(delta, MAX_PULL);
        pullYRef.current = delta;
        setVisualPull(clamped);
    }, []);

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        el.addEventListener('touchmove', handleNativeTouchMove, { passive: false });
        return () => el.removeEventListener('touchmove', handleNativeTouchMove);
    }, [handleNativeTouchMove]);

    const handleTouchEnd = useCallback(async () => {
        if (!armedRef.current) return;
        armedRef.current = false;
        setDragging(false);
        const distance = pullYRef.current;
        pullYRef.current = 0;
        setVisualPull(0);

        if (distance < THRESHOLD || refreshingRef.current || typeof onRefresh !== 'function') return;

        refreshingRef.current = true;
        setRefreshing(true);
        triggerHaptic();
        try {
            await onRefresh();
        } catch (_err) {
            // PullToRefresh owns spinner lifecycle only — there is no error
            // slot in this contract (no onError prop). A screen whose
            // onRefresh can fail is responsible for catching and surfacing
            // that itself (e.g. into its existing error state); swallowing
            // here only prevents an unhandled rejection from a fire-and-
            // forget touchend handler. The spinner clearing on reject is
            // the behaviour under test, not the error's fate.
        } finally {
            refreshingRef.current = false;
            setRefreshing(false);
        }
    }, [onRefresh]);

    const indicatorHeight = refreshing ? THRESHOLD : visualPull;
    const showIndicator = refreshing || visualPull > 0;

    return (
        <div
            ref={containerRef}
            className={`pk-ptr${dragging ? ' pk-ptr-dragging' : ''}`}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={cancelGesture}
        >
            <div
                className="pk-ptr-indicator"
                style={{ height: indicatorHeight }}
                role={refreshing ? 'status' : undefined}
                aria-label={refreshing ? t('ptr_refreshing', {}, 'Refreshing') : undefined}
            >
                {showIndicator && <span className="pk-ptr-spinner" aria-hidden="true" />}
            </div>
            {children}
        </div>
    );
}

export default PullToRefresh;
